import React, { ComponentProps } from "react";

import AppContextValueProvider from "../../../App/Compositions/AppContextValueProvider";
import UserEventContextValueProvider from "../../../App/Compositions/UserEventContextValueProvider";
import ConvertExperimentContextValueProvider from "../../../Experiment/Compositions/ExperimentContextValueProvider";
import FlowStepOutletValueProvider from "../../../Routing/Composition/FlowStepOutletValueProvider";
import { IFlowStepProps } from "../../../Types/IFlow";
import ServiceSelector from "../../CenterSelect/Components/ServiceSelector";
import { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import AppContextReservationCounter from "../../Compositions/AppContextReservationCounter";
import AppContextUseConfirmAppointment from "../../Confirmation/Compositions/AppContextUseConfirmAppointment";
import useGetCalendarEventUrl from "../../Confirmation/Hooks/UseGetCalendarEventUrl";
import IUserEventSource from "../../Types/IUserEventSource";
import SchedulingConfirmStepWrapper, {
  ISchedulingConfirmStepWrapperConfig,
  ISchedulingConfirmStepWrapperExperimentProps,
  ISchedulingConfirmStepWrapperProps
} from "../Components/SchedulingConfirmStepWrapper";
import AppContextSchedulingStepWithAddress from "./AppContextSchedulingStepWithAddress";
import AppContextUseCreateReservation from "./AppContextUseCreateReservation";
import AppContextUseReleaseReservation from "./AppContextUseReleaseReservation";
import DynamicScheduleSlotSelector from "./DynamicScheduleSlotSelector";

const RoutedSchedulingConfirmStepWrapper = FlowStepOutletValueProvider({
  Component: UserEventContextValueProvider({ Component: SchedulingConfirmStepWrapper })
});

const ExperimentContextConfirmWrapper = ConvertExperimentContextValueProvider<
  ISchedulingConfirmStepWrapperExperimentProps &
    Pick<ISchedulingConfirmStepWrapperConfig, "useConfirmAppointment" | "useGetCalendarEventUrl">,
  ComponentProps<typeof RoutedSchedulingConfirmStepWrapper>
>({
  Component: RoutedSchedulingConfirmStepWrapper,
  map: ({ recordBookedAppointment }) => ({
    recordBookedAppointment,
    useConfirmAppointment: AppContextUseConfirmAppointment,
    useGetCalendarEventUrl: useGetCalendarEventUrl
  })
});

const SchedulingStepWithAddress: ISchedulingConfirmStepWrapperConfig["Scheduler"] = props => (
  <AppContextSchedulingStepWithAddress
    {...props}
    useReleaseReservation={AppContextUseReleaseReservation}
    ServiceSelector={ServiceSelector}
    SlotSelector={DynamicScheduleSlotSelector}
    ReservationCounter={AppContextReservationCounter}
  />
);

const AppContextSchedulingConfirmStepWrapper: React.FC<IStepWrapperButtonProps> = AppContextValueProvider<
  Omit<ISchedulingConfirmStepWrapperProps, keyof IFlowStepProps | keyof IUserEventSource> &
    Pick<ISchedulingConfirmStepWrapperConfig, "useCreateReservation" | "Scheduler">,
  ComponentProps<typeof ExperimentContextConfirmWrapper>
>({
  Component: ExperimentContextConfirmWrapper,
  map: ({ bookingController, customerCState, leadIdCState, reservation, router, setReservation }) => {
    if (!bookingController) throw new Error("Booking controller is required for scheduling confirm step.");
    return {
      value: reservation?.slot ?? null,
      onChange: setReservation,
      customerFirstName: customerCState.firstName,
      allCentersUnbookable: !bookingController.isBookingPossible,
      customer: customerCState,
      leadId: leadIdCState,
      reservation: reservation ?? null,
      router,
      useCreateReservation: AppContextUseCreateReservation,
      Scheduler: SchedulingStepWithAddress
    };
  }
});

export default AppContextSchedulingConfirmStepWrapper;
